
import {
  defineComponent,
  PropType,
  computed,
  reactive,
  ref,
  watch,
} from 'vue';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { EnrichedVariable } from '@/components/template-builder/utils/variables-list';
import { formatVariableValue, VariableFormatOptions } from '@/components/template-builder/utils/sanitizer';
import { UserState } from '@/composables/User';
import {
  VariableFormatKind,
  VariableFormatConfig,
  createDefaultFormatConfig,
  buildConfigModifiers,
  buildVariableTag,
  parseVariableValue,
  suggestedFormatFor,
} from './variable-format';

export default defineComponent({
  name: 'VariableFormatPanel',

  components: {
    InputText,
    InputNumber,
    Dropdown,
    Button,
  },

  props: {
    variable: {
      type: Object as PropType<EnrichedVariable>,
      required: true,
    },
  },

  emits: {
    insert: (value: string) => typeof value === 'string',
    back: () => true,
  },

  setup(props) {
    const { t, locale } = useI18n();

    const config = reactive<VariableFormatConfig>(createDefaultFormatConfig());
    const useCustomCurrency = ref(false);

    const suggested = computed(() => suggestedFormatFor(props.variable));

    // Reset the configuration each time the panel opens on another variable, pre-selecting the suggested format
    watch(
      () => props.variable,
      () => {
        Object.assign(config, createDefaultFormatConfig());
        useCustomCurrency.value = false;
        if (suggested.value) {
          config.format = suggested.value;
        }
      },
      { immediate: true },
    );

    const shopCurrency = computed(() => UserState.activeShop?.currency || 'EUR');
    const shopLang = computed(() => UserState.activeShop?.langs?.find((lang) => lang.default)?.id || locale.value);

    let currencyCodesCache: string[] | null = null;
    const currencyCodes = computed<string[]>(() => {
      if (currencyCodesCache === null) {
        try {
          currencyCodesCache = (Intl as any).supportedValuesOf('currency');
        } catch (error) {
          currencyCodesCache = ['EUR', 'USD', 'GBP', 'CHF', 'CAD'];
        }
      }
      return currencyCodesCache as string[];
    });

    const setShopCurrency = () => {
      useCustomCurrency.value = false;
      config.currencyCode = '';
    };

    // All formats stay available for every variable — only the preformatted one is hidden (§ anti double-formatting)
    const formatEntries = computed(() => {
      const entries: { kind: VariableFormatKind; label: string; hint: string }[] = [
        { kind: 'none', label: t('templateBuilder.variableFormat.fmtNone'), hint: t('templateBuilder.variableFormat.fmtNoneHint') },
        { kind: 'currency', label: t('templateBuilder.variableFormat.fmtCurrency'), hint: t('templateBuilder.variableFormat.fmtCurrencyHint') },
        { kind: 'date', label: t('templateBuilder.variableFormat.fmtDate'), hint: t('templateBuilder.variableFormat.fmtDateHint') },
        { kind: 'text', label: t('templateBuilder.variableFormat.fmtText'), hint: t('templateBuilder.variableFormat.fmtTextHint') },
        { kind: 'truncate', label: t('templateBuilder.variableFormat.fmtTruncate'), hint: t('templateBuilder.variableFormat.fmtTruncateHint') },
      ];

      const filtered = entries.filter((entry) => {
        if (props.variable.preformatted === 'currency' && entry.kind === 'currency') {
          return false;
        }
        if (props.variable.preformatted === 'date' && entry.kind === 'date') {
          return false;
        }
        return true;
      });

      // The suggested format is displayed right after "none"
      if (suggested.value) {
        const suggestedIndex = filtered.findIndex((entry) => entry.kind === suggested.value);
        if (suggestedIndex > 1) {
          const [suggestedEntry] = filtered.splice(suggestedIndex, 1);
          filtered.splice(1, 0, suggestedEntry);
        }
      }

      return filtered;
    });

    const datePresets = computed(() => ([
      { key: 'short' as const, label: t('templateBuilder.variableFormat.datePresetShort') },
      { key: 'long' as const, label: t('templateBuilder.variableFormat.datePresetLong') },
      { key: 'datetime' as const, label: t('templateBuilder.variableFormat.datePresetDatetime') },
      { key: 'custom' as const, label: t('templateBuilder.variableFormat.datePresetCustomPill') },
    ]));

    const typeBadge = computed(() => {
      if (props.variable.preformatted) {
        return t('templateBuilder.variableFormat.preformattedBadge');
      }
      if (props.variable.type && ['number', 'decimal'].includes(props.variable.type)) {
        return t('templateBuilder.variableFormat.typeNumber');
      }
      if (props.variable.type && ['date', 'datetime'].includes(props.variable.type)) {
        return t('templateBuilder.variableFormat.typeDate');
      }
      return null;
    });

    const generatedTag = computed(() => buildVariableTag(props.variable.value, config));

    // Live preview : the exact same formatter as the template preview (sanitizer mirror of the backend)
    const previewValue = computed(() => {
      const sampleByFormat: Record<string, string> = {
        currency: '89.99',
        date: new Date().toISOString(),
      };
      let sample = sampleByFormat[config.format];
      if (sample === undefined) {
        if (props.variable.type && ['number', 'decimal'].includes(props.variable.type)) {
          sample = '89.99';
        } else if (props.variable.type && ['date', 'datetime'].includes(props.variable.type)) {
          sample = new Date().toISOString();
        } else {
          sample = t('templateBuilder.variableFormat.sampleText');
        }
      }

      const options: VariableFormatOptions = {
        contactLang: locale.value,
        shopLang: shopLang.value,
        shopCurrency: shopCurrency.value,
      };
      const modifiers = buildConfigModifiers(config);
      const variableKey = parseVariableValue(props.variable.value).path.split('.').pop() || '';

      return formatVariableValue(variableKey, sample, modifiers.length ? modifiers : null, {}, options);
    });

    return {
      t,
      config,
      useCustomCurrency,
      suggested,
      shopCurrency,
      currencyCodes,
      setShopCurrency,
      formatEntries,
      datePresets,
      typeBadge,
      generatedTag,
      previewValue,
    };
  },
});
